import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  toast,
} from '@mlm/ui';
import {
  ACCOUNTING_COA_WRITE_PERM,
  GL_ACCOUNT_TYPE_LABELS,
  GL_ACCOUNT_TYPES,
  GL_BANK_SUBACCOUNT_MAX,
  GL_BANK_SUBACCOUNT_MIN,
} from '@mlm/shared';
import {
  createGlAccount,
  listGlAccounts,
  patchGlAccount,
  type GlAccount,
  type GlAccountType,
} from '../../lib/api';
import { useHasPerm } from '../../lib/auth-store';
import { PageHeader } from './_shared';

/**
 * Chart of Accounts — LIVE (Phase 4 lean GL).
 *
 * A tenant can ADD accounts (PP&E, a loan, a new expense line) — the
 * statements classify purely by `type`, so a new account files itself. But
 * codes are STABLE (the posting catalog keys on them) and types are immutable,
 * so the editor only ever renames, re-orders, or (de)activates. Deactivating
 * hides an account from pickers; the poster still books to it — the books
 * never reroute because someone tidied the chart. The 1060–1099 "Cash in Bank"
 * sub-accounts appear here automatically as the poster maps each cash/bank
 * funds account.
 */

/** Poster-minted, as opposed to added by a human — both are non-system. */
function isBankSubaccount(a: GlAccount): boolean {
  const n = Number(a.code);
  return a.type === 'asset' && n >= GL_BANK_SUBACCOUNT_MIN && n <= GL_BANK_SUBACCOUNT_MAX;
}
export function ChartOfAccountsPage() {
  const canWrite = useHasPerm(ACCOUNTING_COA_WRITE_PERM);
  const [q, setQ] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<GlAccount | null>(null);
  const [creating, setCreating] = useState(false);

  const accountsQ = useQuery({ queryKey: ['gl-accounts'], queryFn: listGlAccounts });
  const all = accountsQ.data?.rows ?? [];

  const needle = q.trim().toLowerCase();
  const rows = all.filter(
    (a) =>
      (showInactive || a.active) &&
      (!needle || a.code.includes(needle) || a.name.toLowerCase().includes(needle)),
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Chart of Accounts"
        subtitle="Add your own accounts; a code and type are fixed once saved, because the automatic posting rules key on them. Names, order and active state stay editable."
        actions={
          canWrite ? <Button onClick={() => setCreating(true)}>New account</Button> : undefined
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          <div className="w-64 space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="coa-search">
              Search
            </label>
            <Input
              id="coa-search"
              className="h-9"
              placeholder="Account code or name"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 pb-2 text-sm">
            <Checkbox checked={showInactive} onCheckedChange={(v) => setShowInactive(v === true)} />
            Show inactive
          </label>
        </CardContent>
      </Card>

      {accountsQ.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        GL_ACCOUNT_TYPES.map((type) => {
          const group = rows.filter((a) => a.type === type);
          if (group.length === 0) return null;
          return (
            <Card key={type}>
              <CardHeader>
                <CardTitle>
                  {GL_ACCOUNT_TYPE_LABELS[type]}{' '}
                  <span className="text-sm font-normal text-muted-foreground">
                    ({group.length})
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="w-24 px-4 py-2">Code</th>
                        <th className="px-4 py-2">Account</th>
                        <th className="w-28 px-4 py-2">Status</th>
                        {canWrite ? <th className="w-20 px-4 py-2" /> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {group.map((a) => (
                        <tr key={a.id} className="border-b last:border-0 hover:bg-muted/30">
                          <td className="px-4 py-2 font-mono text-xs">{a.code}</td>
                          <td className="px-4 py-2">
                            <div>{a.name}</div>
                            {!a.isSystem ? (
                              <div className="text-xs text-muted-foreground">
                                {isBankSubaccount(a)
                                  ? 'Added automatically for a Cash & Bank account'
                                  : 'Added by your team'}
                              </div>
                            ) : null}
                          </td>
                          <td className="px-4 py-2">
                            <Badge variant={a.active ? 'success' : 'muted'}>
                              {a.active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          {canWrite ? (
                            <td className="px-4 py-2 text-right">
                              <Button size="sm" variant="outline" onClick={() => setEditing(a)}>
                                Edit
                              </Button>
                            </td>
                          ) : null}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          );
        })
      )}

      {editing ? <AccountEditDialog account={editing} onClose={() => setEditing(null)} /> : null}
      {creating ? <AccountCreateDialog onClose={() => setCreating(false)} /> : null}
    </div>
  );
}

/**
 * Where a code lands on the statements. The Balance Sheet and Trial Balance go
 * purely by type, but the Income Statement SUB-SECTIONS by code range, so the
 * number a user picks decides whether an expense reads as cost of goods sold
 * or as an operating expense. Say so before they save — the code is immutable.
 */
function placementHint(code: string, type: GlAccountType): string | null {
  if (!/^\d{4}$/.test(code)) return null;
  if (type === 'asset' || type === 'liability' || type === 'equity') {
    const section = type === 'asset' ? 'Assets' : type === 'liability' ? 'Liabilities' : 'Equity';
    return `Files under ${section} on the Balance Sheet.`;
  }
  if (type === 'income') {
    return code >= '4300' && code < '5000'
      ? 'Files under Other income on the Income Statement.'
      : 'Files under Revenue on the Income Statement.';
  }
  return code < '6000'
    ? 'Files under Cost of goods sold on the Income Statement.'
    : 'Files under Operating expenses on the Income Statement.';
}

function createErrText(error: unknown): string {
  const code = (error as { response?: { data?: { error?: string } } } | null)?.response?.data
    ?.error;
  if (code === 'gl_account_code_taken') return 'That code is already in the chart.';
  if (code === 'gl_account_code_reserved') {
    return 'That code is reserved. 1000–1099 is the cash and bank family, which the system extends on its own.';
  }
  if (code === 'gl_invalid_account_code') return 'Use a four-digit code.';
  return 'Could not add the account.';
}

function AccountCreateDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState<GlAccountType>('asset');

  const save = useMutation({
    mutationFn: () => createGlAccount({ code: code.trim(), name: name.trim(), type }),
    onSuccess: (account) => {
      toast.success(`Added ${account.code} · ${account.name}.`);
      void qc.invalidateQueries({ queryKey: ['gl-accounts'] });
      onClose();
    },
    onError: (err) => toast.error(createErrText(err)),
  });

  const hint = placementHint(code.trim(), type);
  const valid = /^\d{4}$/.test(code.trim()) && name.trim().length > 0;

  return (
    <Dialog open onOpenChange={(o) => !o && !save.isPending && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New account</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-3">
            <div className="w-28 space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="coa-new-code">
                Code
              </label>
              <Input
                id="coa-new-code"
                className="font-mono"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="1300"
                inputMode="numeric"
              />
            </div>
            <div className="flex-1 space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="coa-new-name">
                Name
              </label>
              <Input
                id="coa-new-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Equipment"
                maxLength={120}
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="coa-new-type">
              Type
            </label>
            <select
              id="coa-new-type"
              value={type}
              onChange={(e) => setType(e.target.value as GlAccountType)}
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
            >
              {GL_ACCOUNT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {GL_ACCOUNT_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>
          <p className="text-xs text-muted-foreground">
            {hint ?? 'Pick a four-digit code in the band you want it to sort into.'} The code and
            type cannot be changed afterwards — the posting rules key on them.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || !valid}>
            {save.isPending ? 'Adding…' : 'Add account'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AccountEditDialog({ account, onClose }: { account: GlAccount; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(account.name);
  const [active, setActive] = useState(account.active);

  const save = useMutation({
    mutationFn: () => patchGlAccount(account.id, { name: name.trim(), active }),
    onSuccess: () => {
      toast.success('Account updated.');
      void qc.invalidateQueries({ queryKey: ['gl-accounts'] });
      onClose();
    },
    onError: () => toast.error('Could not update the account.'),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Edit {account.code} · {account.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
          </div>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              className="mt-0.5"
              checked={active}
              onCheckedChange={(v) => setActive(v === true)}
            />
            <span>
              Active
              <span className="block text-xs text-muted-foreground">
                Inactive accounts are hidden from pickers, but existing rules still post to them so
                the books never silently reroute.
              </span>
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => save.mutate()}
            disabled={save.isPending || name.trim().length === 0}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
